/**
 * @author NikolaV
 *
 * modified from 'airbnb/react-outside-click-handle'
 *      add support for <fragments>
 *      convert to typescript
 */

import * as React from 'react';
import { addEventListener } from 'consolidated-events';


type Props = {
    onOutsideClick: (evt) => void
    disabled?: boolean,
    useCapture?: boolean,
    children?: React.ReactNode
};

export class OutsideClick extends React.Component<Props> {
    private childNode: HTMLElement;
    private removeMouseDown: Function = null;
    private removeMouseUp: Function = null;

    static defaultProps: Partial<Props> = {
        disabled: false,

        // `useCapture` is set to true by default so that a `stopPropagation` in the
        // children will not prevent all outside click handlers from firing - maja
        useCapture: true
    };

    componentDidMount() {
        const { disabled, useCapture } = this.props;

        if (!disabled) this.addMouseDownEventListener(useCapture);
    }

    componentDidUpdate(prevProps: Props) {
        const { disabled, useCapture } = this.props;

        if (disabled !== prevProps.disabled) {
            if (this.props.disabled) {
                this.removeEventListeners();
            }
            else {
                this.addMouseDownEventListener(useCapture);
            }
        }
    }

    componentWillUnmount() {
        this.removeEventListeners();
    }

    // Use mousedown/mouseup to enforce that clicks remain outside the root's
    // descendant tree, even when dragged. This should also get triggered on
    // touch devices.
    onMouseDown = (e): void => {
        const { useCapture } = this.props;

        const isDescendantOfRoot = this.childNode && this.childNode.contains(e.target);
        if (!isDescendantOfRoot) {
            this.removeMouseUp = addEventListener(
                document,
                'mouseup',
                this.onMouseUp,
                { capture: useCapture },
            );
        }
    }

    // Use mousedown/mouseup to enforce that clicks remain outside the root's
    // descendant tree, even when dragged. This should also get triggered on
    // touch devices.
    onMouseUp = (e): void => {
        const { onOutsideClick } = this.props;

        const isDescendantOfRoot = this.childNode && this.childNode.contains(e.target);
        if (this.removeMouseUp) this.removeMouseUp();
        this.removeMouseUp = null;

        if (!isDescendantOfRoot) {
            onOutsideClick(e);
        }
    }

    addMouseDownEventListener = (useCapture: boolean): void => {
        this.removeMouseDown = addEventListener(
            document,
            'mousedown',
            this.onMouseDown,
            { capture: useCapture },
        );
    }

    removeEventListeners = (): void => {
        if (this.removeMouseDown) this.removeMouseDown();
        if (this.removeMouseUp) this.removeMouseUp();
    }

    setChildNodeRef = (node: HTMLElement): void => {
        this.childNode = node;
    }

    render() {
        return React.cloneElement(React.Children.only(this.props.children) as React.ReactElement, {
            ref: this.setChildNodeRef
        });
    }
}
